/**
 * Card Tag Rules — admin override layer for the Deck Context Resolution
 * Layer (mobile/lib/deckContextResolver.js + frontend equivalent). Rules
 * created here take priority over the app's static DECK_CONTEXT_CARD_LOGIC
 * catalog; GET /api/tag-rules/active (public) is what mobile/web actually
 * fetch on deck load.
 *
 * contextRequirements / whenSupported / whenUnsupported are edited as raw
 * JSON-ish text fields rather than a deeply nested form — same tradeoff
 * AdminGenesys.jsx makes for its cards list, and these objects can be
 * arbitrarily shaped (extraDeck / mainDeck / locationRequirements).
 *
 * The tester panel calls POST /api/tag-rules/test, a simplified server-side
 * port of the resolver's requirement checks (name/type/tag matching for
 * Extra Deck requirements — NOT the full feasibility engine that handles
 * level/material math for cards like Instant Fusion). Good enough to sanity
 * check a rule; not a guarantee it'll behave identically in the live app.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import { SkeletonTable } from "../components/ui/Skeleton";
import StatusBadge from "../components/ui/StatusBadge";
import {
  Plus, Tags, Trash2, Pencil, FlaskConical, X, Save, ChevronDown, ChevronRight, BookOpen,
} from "lucide-react";
import {
  apiListTagRules, apiCreateTagRule, apiUpdateTagRule, apiDeleteTagRule, apiTestTagRule,
} from "../lib/cloudSync";

const emptyForm = () => ({
  cardName: "",
  baseTags: "",
  contextRequirementsText: "{}",
  whenSupportedAdd: "",
  whenSupportedRemove: "",
  whenSupportedExplanation: "",
  whenUnsupportedAdd: "",
  whenUnsupportedRemove: "",
  whenUnsupportedExplanation: "",
  confidence: 0.85,
  active: true,
});

const csvToList = (s) => (s || "").split(",").map((t) => t.trim()).filter(Boolean);
const listToCsv = (l) => (l || []).join(", ");

const ruleToForm = (rule) => ({
  cardName: rule.cardName || "",
  baseTags: listToCsv(rule.baseTags),
  contextRequirementsText: JSON.stringify(rule.contextRequirements || {}, null, 2),
  whenSupportedAdd: listToCsv(rule.whenSupported?.addTags),
  whenSupportedRemove: listToCsv(rule.whenSupported?.removeTags),
  whenSupportedExplanation: rule.whenSupported?.explanation || "",
  whenUnsupportedAdd: listToCsv(rule.whenUnsupported?.addTags),
  whenUnsupportedRemove: listToCsv(rule.whenUnsupported?.removeTags),
  whenUnsupportedExplanation: rule.whenUnsupported?.explanation || "",
  confidence: rule.confidence ?? 0.85,
  active: rule.active !== false,
});

const formToRule = (f) => ({
  cardName: f.cardName.trim(),
  baseTags: csvToList(f.baseTags),
  contextRequirements: JSON.parse(f.contextRequirementsText || "{}"),
  whenSupported: {
    addTags: csvToList(f.whenSupportedAdd),
    removeTags: csvToList(f.whenSupportedRemove),
    explanation: f.whenSupportedExplanation.trim(),
  },
  whenUnsupported: {
    addTags: csvToList(f.whenUnsupportedAdd),
    removeTags: csvToList(f.whenUnsupportedRemove),
    explanation: f.whenUnsupportedExplanation.trim(),
  },
  confidence: Number(f.confidence) || 0.85,
  active: !!f.active,
});

// ── Built-in tag reference data (mirrors mobile/lib/constants.js TAG_GROUPS) ──
const TAG_GROUPS = [
  {
    name: "Core Combo Role",
    description: "How this card fits into your combo lines",
    tags: [
      { id: "starter", label: "Starter", description: "Begins your main engine or combo line" },
      { id: "extender", label: "Extender", description: "Continues plays after the first action" },
      { id: "one-card-combo", label: "One-card combo", description: "Creates meaningful setup by itself" },
      { id: "two-card-combo", label: "Two-card combo piece", description: "Needs a specific partner card" },
      { id: "payoff", label: "Payoff", description: "The card or line you are trying to access" },
      { id: "enabler", label: "Enabler", description: "Unlocks plays but may not start them alone" },
    ],
  },
  {
    name: "Card Function",
    description: "What this card does mechanically",
    tags: [
      { id: "searcher", label: "Searcher", description: "Adds cards from deck to hand" },
      { id: "recursion", label: "Recursion", description: "Reuses spent resources from GY" },
      { id: "engine-requirement", label: "Engine requirement", description: "Needed for engine function" },
      { id: "normal-summon", label: "Normal Summon", description: "Usually wants your Normal Summon" },
      { id: "follow-up", label: "Follow-up", description: "Helps next turn after combo resolves" },
      { id: "recovery", label: "Recovery", description: "Helps rebuild after interruption" },
    ],
  },
  {
    name: "Interaction / Disruption",
    description: "How this card interacts with the opponent",
    tags: [
      { id: "hand-trap", label: "Hand trap", description: "Interrupts from hand on opponent's turn" },
      { id: "board-breaker", label: "Board breaker", description: "Helps crack established boards" },
      { id: "removal", label: "Removal", description: "Answers threats already on field" },
      { id: "protection", label: "Protection", description: "Helps your cards survive interruption" },
      { id: "floodgate", label: "Floodgate", description: "Restricts opponent's options continuously" },
      { id: "tech-card", label: "Tech card", description: "Chosen for matchup or format reasons" },
    ],
  },
  {
    name: "Turn Preference",
    description: "When this card performs best",
    tags: [
      { id: "going-first", label: "Going first", description: "Best when setting up first" },
      { id: "going-second", label: "Going second", description: "Best when playing into a board" },
      { id: "flexible", label: "Flexible", description: "Good in either turn order" },
    ],
  },
  {
    name: "Risk / Consistency",
    description: "How this card affects deck consistency",
    tags: [
      { id: "brick", label: "Brick", description: "Awkward or weak to open in hand" },
      { id: "garnet", label: "Garnet", description: "Bad to draw, needs to stay in deck" },
      { id: "good-opener", label: "Good opener", description: "Strong in opening hand" },
      { id: "situational", label: "Situational", description: "Strong only in certain game states" },
    ],
  },
  {
    name: "Deck Context (Auto-resolved)",
    description: "Resolved dynamically by checking this card against the rest of the deck list. These tags are never manually set — they are assigned by the context resolution engine.",
    context: true,
    tags: [
      { id: "extra-deck-access", label: "Extra Deck access", description: "Reaches into the Extra Deck for a Fusion/Synchro/Xyz/Link play" },
      { id: "extra-deck-payoff", label: "Extra Deck payoff", description: "The Extra Deck card a starter is trying to reach" },
      { id: "extra-deck-bridge", label: "Extra Deck bridge", description: "Connects a starter's output to its Extra Deck payoff" },
      { id: "conditional-starter", label: "Conditional starter", description: "Starts plays only if a specific condition or target is met" },
      { id: "unsupported", label: "Unsupported", description: "Required deck-context support is missing for this card's role" },
      { id: "inactive", label: "Inactive", description: "Card's main role cannot currently resolve given the deck list" },
    ],
  },
];

const RESOLUTION_PRIORITY = [
  { label: "1 · Admin override rule", desc: "Rules created here — highest priority. Applied first, overrides everything below.", highlight: true },
  { label: "2 · App static card logic", desc: "Built-in DECK_CONTEXT_CARD_LOGIC catalog (e.g. Instant Fusion, Ready Fusion, Pot of Prosperity). Hard-coded in the app bundle." },
  { label: "3 · AI suggested logic", desc: "Tags suggested by the AI auto-breakdown endpoint and merged in by the builder before resolution." },
  { label: "4 · User / manual tags", desc: "Tags the user set directly on a card. Always preserved — never overwritten by any layer above." },
];

const TagReference = () => {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  return (
    <div className="card-celestial rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-surface/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-celestial-muted" />
          <span className="text-sm font-medium text-celestial-text">Built-in Tag Reference & AI Resolution System</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-celestial-muted" /> : <ChevronRight className="w-4 h-4 text-celestial-muted" />}
      </button>

      {open && (
        <div className="border-t border-celestial-border/10 px-5 pb-5 pt-4 space-y-5">
          {/* Resolution priority */}
          <div>
            <div className="text-xs font-semibold text-celestial-muted uppercase tracking-wide mb-2">Context Resolution Priority</div>
            <div className="space-y-1.5">
              {RESOLUTION_PRIORITY.map((step) => (
                <div key={step.label} className={`flex gap-3 text-xs rounded-lg px-3 py-2 ${step.highlight ? "bg-glow-mid/10 border border-glow-mid/20" : "bg-surface/30"}`}>
                  <span className={`font-mono font-semibold shrink-0 ${step.highlight ? "text-glow-light" : "text-celestial-muted"}`}>{step.label}</span>
                  <span className="text-celestial-muted/80">{step.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tag groups */}
          <div>
            <div className="text-xs font-semibold text-celestial-muted uppercase tracking-wide mb-2">All Tag IDs</div>
            <div className="space-y-1.5">
              {TAG_GROUPS.map((group) => (
                <div key={group.name} className={`rounded-xl border overflow-hidden ${group.context ? "border-glow-mid/25 bg-glow-mid/5" : "border-celestial-border/15"}`}>
                  <button
                    onClick={() => setOpenGroup(openGroup === group.name ? null : group.name)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-left"
                  >
                    <div>
                      <span className="text-xs font-semibold text-celestial-text">{group.name}</span>
                      {group.context && (
                        <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-glow-mid/20 text-glow-light font-medium">auto-resolved</span>
                      )}
                      <p className="text-[11px] text-celestial-muted/70 mt-0.5">{group.description}</p>
                    </div>
                    {openGroup === group.name
                      ? <ChevronDown className="w-3.5 h-3.5 text-celestial-muted shrink-0" />
                      : <ChevronRight className="w-3.5 h-3.5 text-celestial-muted shrink-0" />}
                  </button>
                  {openGroup === group.name && (
                    <div className="border-t border-celestial-border/10 divide-y divide-celestial-border/5">
                      {group.tags.map((tag) => (
                        <div key={tag.id} className="flex items-start gap-3 px-4 py-2">
                          <code className="text-[11px] font-mono text-glow-light bg-glow-light/10 px-1.5 py-0.5 rounded shrink-0 mt-0.5">{tag.id}</code>
                          <div>
                            <span className="text-xs font-medium text-celestial-text">{tag.label}</span>
                            <p className="text-[11px] text-celestial-muted/70">{tag.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const defaultSampleDeck = () =>
  JSON.stringify(
    [
      { name: "Sample Starter Card", section: "main", type: "Effect Monster", tags: [] },
      { name: "Some Other Card", section: "main", type: "Effect Monster", tags: [] },
    ],
    null,
    2
  );

const TagRules = () => {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [editingId, setEditingId] = useState(null); // null = closed, "new" = creating
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [sampleDeckText, setSampleDeckText] = useState(defaultSampleDeck());
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await apiListTagRules();
      setRules(data?.rules || []);
    } catch (e) {
      setLoadError(e?.message || "Failed to load tag rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm());
    setFormError("");
    setTestResult(null);
    setTestError("");
    setEditingId("new");
  };

  const openEdit = (rule) => {
    setForm(ruleToForm(rule));
    setFormError("");
    setTestResult(null);
    setTestError("");
    setEditingId(rule.id);
  };

  const closeEditor = () => setEditingId(null);

  const onSave = async () => {
    setFormError("");
    let rule;
    try {
      rule = formToRule(form);
    } catch (e) {
      setFormError("Context requirements must be valid JSON.");
      return;
    }
    if (!rule.cardName) {
      setFormError("Card name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId === "new") {
        await apiCreateTagRule(rule);
      } else {
        await apiUpdateTagRule(editingId, rule);
      }
      await load();
      closeEditor();
    } catch (e) {
      setFormError(e?.message || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (rule) => {
    if (!window.confirm(`Delete the tag rule for "${rule.cardName}"?`)) return;
    try {
      await apiDeleteTagRule(rule.id);
      await load();
      if (editingId === rule.id) closeEditor();
    } catch (e) {
      setLoadError(e?.message || "Failed to delete rule");
    }
  };

  const onTest = async () => {
    setTestError("");
    setTestResult(null);
    let rule, sampleDeck;
    try {
      rule = formToRule(form);
    } catch {
      setTestError("Context requirements must be valid JSON.");
      return;
    }
    if (!rule.cardName) {
      setTestError("Card name is required before testing.");
      return;
    }
    try {
      sampleDeck = JSON.parse(sampleDeckText || "[]");
      if (!Array.isArray(sampleDeck)) throw new Error("must be an array");
    } catch {
      setTestError("Sample deck must be a JSON array of { name, section, type, tags }.");
      return;
    }
    setTesting(true);
    try {
      const result = await apiTestTagRule(rule, sampleDeck);
      setTestResult(result);
    } catch (e) {
      setTestError(e?.message || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <PageHeader
        title="Card Tag Rules"
        subtitle="Admin override layer for deck-context tag resolution — takes priority over the app's built-in static rule catalog."
        actions={
          <Button onClick={openNew} className="btn-celestial rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> New rule
          </Button>
        }
      />

      <TagReference />

      {loadError && (
        <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{loadError}</div>
      )}

      {loading ? (
        <SkeletonTable rows={4} />
      ) : rules.length === 0 ? (
        <div className="card-celestial rounded-2xl">
          <EmptyState
            icon={Tags}
            title="No tag rules yet."
            description="This collection is empty out of the box — clients fall through to the app's static card-logic catalog until you create a rule here."
            action={
              <Button onClick={openNew} className="btn-celestial rounded-lg px-3 py-1.5 text-xs font-medium mt-1">
                Create the first rule
              </Button>
            }
          />
        </div>
      ) : (
        <div className="card-celestial rounded-2xl divide-y divide-celestial-border/10">
          {rules.map((rule) => (
            <div key={rule.id} className="px-5 py-3.5 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-celestial-text truncate">{rule.cardName}</span>
                  <StatusBadge status={rule.active ? "active" : "archived"}>
                    {rule.active ? "active" : "inactive"}
                  </StatusBadge>
                </div>
                <div className="text-xs text-celestial-muted/70 mt-0.5 truncate">
                  {(rule.baseTags || []).join(", ") || "no base tags"} · confidence {rule.confidence ?? "—"}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => openEdit(rule)}
                  className="p-2 rounded-lg text-celestial-muted hover:text-celestial-text hover:bg-surface/60"
                  aria-label={`Edit rule for ${rule.cardName}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onDelete(rule)}
                  className="p-2 rounded-lg text-celestial-muted hover:text-danger hover:bg-danger/10"
                  aria-label={`Delete rule for ${rule.cardName}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingId && (
        <div className="card-celestial rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-celestial-text">
              {editingId === "new" ? "New tag rule" : `Editing: ${form.cardName}`}
            </h2>
            <button onClick={closeEditor} className="p-1.5 rounded-lg text-celestial-muted hover:text-celestial-text">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Card name (exact)" value={form.cardName} onChange={(v) => setForm({ ...form, cardName: v })} placeholder="Instant Fusion" />
            <Field label="Confidence (0–1)" type="number" value={form.confidence} onChange={(v) => setForm({ ...form, confidence: v })} />
            <Field label="Base tags (comma-separated)" value={form.baseTags} onChange={(v) => setForm({ ...form, baseTags: v })} placeholder="extra-deck-access, enabler" />
            <label className="flex items-center gap-2 text-sm text-celestial-muted self-end pb-2.5">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active (live to clients via /api/tag-rules/active)
            </label>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-celestial-muted">
              Context requirements (JSON — extraDeck / mainDeck / locationRequirements)
            </label>
            <textarea
              value={form.contextRequirementsText}
              onChange={(e) => setForm({ ...form, contextRequirementsText: e.target.value })}
              rows={6}
              spellCheck={false}
              className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text font-mono text-xs focus:outline-none focus:border-glow-mid/50"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3 p-4 rounded-xl bg-success/5 border border-success/20">
              <div className="text-xs font-semibold text-success">When supported</div>
              <Field label="Add tags" value={form.whenSupportedAdd} onChange={(v) => setForm({ ...form, whenSupportedAdd: v })} placeholder="starter, one-card-combo" />
              <Field label="Remove tags" value={form.whenSupportedRemove} onChange={(v) => setForm({ ...form, whenSupportedRemove: v })} placeholder="brick, garnet" />
              <Field label="Explanation" value={form.whenSupportedExplanation} onChange={(v) => setForm({ ...form, whenSupportedExplanation: v })} placeholder="Valid target found." />
            </div>
            <div className="space-y-3 p-4 rounded-xl bg-danger/5 border border-danger/20">
              <div className="text-xs font-semibold text-danger">When unsupported</div>
              <Field label="Add tags" value={form.whenUnsupportedAdd} onChange={(v) => setForm({ ...form, whenUnsupportedAdd: v })} placeholder="unsupported, inactive, brick" />
              <Field label="Remove tags" value={form.whenUnsupportedRemove} onChange={(v) => setForm({ ...form, whenUnsupportedRemove: v })} placeholder="starter, extender" />
              <Field label="Explanation" value={form.whenUnsupportedExplanation} onChange={(v) => setForm({ ...form, whenUnsupportedExplanation: v })} placeholder="No valid target found." />
            </div>
          </div>

          {formError && (
            <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{formError}</div>
          )}

          <div className="flex items-center gap-2">
            <Button onClick={onSave} disabled={saving} className="btn-celestial rounded-xl px-4 py-2 flex items-center gap-2">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save rule"}
            </Button>
          </div>

          {/* Tester */}
          <div className="pt-4 border-t border-celestial-border/10 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-celestial-text">
              <FlaskConical className="w-4 h-4 text-celestial-muted" /> Test against a sample decklist
            </div>
            <p className="text-xs text-celestial-muted/70">
              Approximated check — Extra Deck requirements use name/type/tag matching here, not the full
              level/material feasibility engine the live app uses for cards like Instant Fusion.
            </p>
            <textarea
              value={sampleDeckText}
              onChange={(e) => setSampleDeckText(e.target.value)}
              rows={8}
              spellCheck={false}
              className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text font-mono text-xs focus:outline-none focus:border-glow-mid/50"
            />
            <Button onClick={onTest} disabled={testing} variant="outline" className="rounded-xl px-4 py-2 border-celestial-border/30">
              {testing ? "Testing…" : "Run test"}
            </Button>

            {testError && (
              <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{testError}</div>
            )}
            {testResult && (
              <div className="rounded-xl border border-celestial-border/20 bg-surface/40 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <StatusBadge status={testResult.status === "supported" ? "resolved" : "open"}>{testResult.status}</StatusBadge>
                  <span className="text-celestial-muted">{testResult.reason}</span>
                </div>
                <div className="text-xs text-celestial-muted">
                  Resulting tags: <span className="text-celestial-text">{(testResult.resultingTags || []).join(", ") || "—"}</span>
                </div>
                {testResult.matchedCards?.length > 0 && (
                  <div className="text-xs text-celestial-muted">Matched: {testResult.matchedCards.join(", ")}</div>
                )}
                {testResult.missingCards?.length > 0 && (
                  <div className="text-xs text-celestial-muted">Missing: {testResult.missingCards.join(", ")}</div>
                )}
                <div className="text-[11px] text-celestial-muted/60 pt-1">{testResult.note}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", placeholder }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-celestial-muted">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 focus:outline-none focus:border-glow-mid/50"
    />
  </div>
);

export default TagRules;
